import React, { useState } from 'react';
import {
  LayoutDashboard, Plus, ShoppingCart, DollarSign, Package, AppWindow,
  Shield, ShieldCheck, SlidersHorizontal, Search, Barcode, Boxes, Mail,
  Users, ChevronLeft, ChevronRight, Menu, X, LogOut, ChevronDown,
  UserCircle
} from 'lucide-react';

const TC_LOGO = 'https://customer-assets.emergentagent.com/job_warranty-approval/artifacts/1iugy51y_Gold.png';

const ROLE_LABELS = {
  tc_admin: 'TC Administrator',
  elite_partner: 'Elite TC Partner',
  installation_center: 'Installation Center',
  sales_rep: 'Sales Representative',
};

const buildNav = (role, t) => {
  if (role === 'tc_admin') {
    return [
      { group: 'Overview', items: [{ k: 'dashboard', label: t('dashboard'), icon: LayoutDashboard }] },
      { group: 'Sales', items: [
        { k: 'create-order', label: t('addOrder'), icon: Plus },
        { k: 'orders', label: t('ordersManagement'), icon: ShoppingCart },
        { k: 'pricing', label: t('pricing'), icon: DollarSign },
      ] },
      { group: 'Products', items: [{ k: 'products', label: t('products'), icon: Package }] },
      { group: 'TC Software', items: [{ k: 'software', label: t('software'), icon: AppWindow }] },
      { group: 'Certificates & Warranty', items: [
        { k: 'warranty', label: t('warranty'), icon: Shield },
        { k: 'warranty-admin', label: t('warrantyManagement'), icon: ShieldCheck },
        { k: 'coverage-rules', label: 'Coverage Rules', icon: SlidersHorizontal },
      ] },
      { group: 'Operations', items: [
        { k: 'serial-search', label: t('serialSearch'), icon: Search },
        { k: 'production-barcodes', label: t('productionBarcodesTab'), icon: Barcode },
        { k: 'roll-usage', label: 'Roll Usage', icon: Boxes },
      ] },
      { group: 'Communication', items: [{ k: 'email-center', label: t('emailCenterTab'), icon: Mail }] },
      { group: 'Administration', items: [{ k: 'users', label: t('users'), icon: Users }] },
    ];
  }
  if (role === 'sales_rep') {
    return [
      { group: 'Overview', items: [{ k: 'dashboard', label: t('dashboard'), icon: LayoutDashboard }] },
      { group: 'Clients', items: [
        { k: 'my-clients', label: t('myClients'), icon: Users },
      ] },
      { group: 'Sales', items: [
        { k: 'create-order', label: t('addOrder'), icon: Plus },
        { k: 'orders', label: t('ordersManagement'), icon: ShoppingCart },
      ] },
      { group: 'Catalog', items: [{ k: 'products', label: t('products'), icon: Package }] },
      { group: 'TC Software', items: [{ k: 'software', label: t('software'), icon: AppWindow }] },
      { group: 'Warranty', items: [{ k: 'warranty', label: t('warranty'), icon: Shield }] },
    ];
  }
  // dealer (installation_center / elite_partner)
  return [
    { group: 'Overview', items: [{ k: 'dashboard', label: t('dashboard'), icon: LayoutDashboard }] },
    { group: 'Orders', items: [
      { k: 'create-order', label: t('newOrder'), icon: Plus },
      { k: 'orders', label: t('myOrders'), icon: ShoppingCart },
    ] },
    { group: 'Catalog', items: [{ k: 'products', label: t('products'), icon: Package }] },
    { group: 'TC Software', items: [{ k: 'software', label: t('software'), icon: AppWindow }] },
    { group: 'Warranty', items: [{ k: 'warranty', label: t('warranty'), icon: Shield }] },
  ];
};

const NavItem = ({ item, active, collapsed, onClick }) => {
  const Icon = item.icon;
  return (
    <button
      onClick={onClick}
      title={collapsed ? item.label : undefined}
      data-testid={`nav-${item.k}`}
      className={`group relative flex items-center gap-3 w-full rounded-lg px-3 py-2 text-sm transition-colors duration-150
        ${active
          ? 'bg-[#C6A15B]/12 text-[#E7CB85] font-medium'
          : 'text-slate-400 hover:text-slate-100 hover:bg-white/[0.04]'}
        ${collapsed ? 'justify-center px-0' : ''}`}
    >
      {active && !collapsed && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-[#C6A15B]" />}
      <Icon className={`w-[18px] h-[18px] shrink-0 ${active ? 'text-[#C6A15B]' : ''}`} />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </button>
  );
};

export const AppShell = ({ user, activeTab, setActiveTab, onLogout, t, languageToggle, children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const role = user?.role;
  const nav = buildNav(role, t);
  const flatItems = nav.flatMap((g) => g.items);
  const activeItem = flatItems.find((i) => i.k === activeTab);
  const pageTitle = activeItem?.label || t('dashboard');

  const initials = (user?.full_name || user?.username || 'TC')
    .split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();

  const go = (k) => { setActiveTab(k); setMobileOpen(false); };

  const SidebarInner = ({ isCollapsed }) => (
    <div className="flex flex-col h-full">
      {/* Branding */}
      <div className={`flex items-center gap-3 px-4 h-16 border-b border-white/[0.06] ${isCollapsed ? 'justify-center px-0' : ''}`}>
        <img src={TC_LOGO} alt="TC" className="w-9 h-6 object-contain" />
        {!isCollapsed && (
          <div className="leading-tight">
            <p className="text-white font-semibold text-sm tracking-wide">TC Protection Film</p>
            <p className="text-[11px] text-[#C6A15B]">{t('distributionSystem')}</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-5 tc-sidebar-scroll">
        {nav.map((grp) => (
          <div key={grp.group}>
            {!isCollapsed && (
              <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">{grp.group}</p>
            )}
            <div className="space-y-0.5">
              {grp.items.map((item) => (
                <NavItem key={item.k} item={item} collapsed={isCollapsed}
                  active={activeTab === item.k} onClick={() => go(item.k)} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Collapse toggle (desktop) */}
      <div className="hidden lg:flex border-t border-white/[0.06] p-2">
        <button
          onClick={() => setCollapsed((c) => !c)}
          data-testid="sidebar-collapse-toggle"
          className="flex items-center justify-center gap-2 w-full rounded-lg px-3 py-2 text-xs text-slate-400 hover:text-slate-100 hover:bg-white/[0.04] transition-colors"
        >
          {isCollapsed ? <ChevronRight className="w-4 h-4" /> : (<><ChevronLeft className="w-4 h-4" /> Collapse</>)}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#090A0C] text-slate-200">
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col fixed inset-y-0 left-0 z-30 bg-[#0C0E11] border-r border-white/[0.06] transition-[width] duration-200
          ${collapsed ? 'w-[76px]' : 'w-[260px]'}`}
      >
        <SidebarInner isCollapsed={collapsed} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-[270px] bg-[#0C0E11] border-r border-white/[0.06] shadow-2xl">
            <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-3 text-slate-400 hover:text-white z-10" data-testid="mobile-sidebar-close">
              <X className="w-5 h-5" />
            </button>
            <SidebarInner isCollapsed={false} />
          </aside>
        </div>
      )}

      {/* Main column */}
      <div className={`transition-[padding] duration-200 ${collapsed ? 'lg:pl-[76px]' : 'lg:pl-[260px]'}`}>
        {/* Top header */}
        <header className="sticky top-0 z-20 h-16 bg-[#0C0E11]/85 backdrop-blur-xl border-b border-white/[0.06] flex items-center gap-3 px-4 sm:px-6">
          <button className="lg:hidden text-slate-300 hover:text-white" onClick={() => setMobileOpen(true)} data-testid="mobile-sidebar-open">
            <Menu className="w-6 h-6" />
          </button>

          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-semibold text-white truncate" data-testid="page-title">{pageTitle}</h1>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 ml-auto">
            {languageToggle}

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen((o) => !o)}
                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-white/[0.04] transition-colors"
                data-testid="user-menu-trigger"
              >
                <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C6A15B] to-[#8a6f3a] text-black text-xs font-bold flex items-center justify-center">{initials}</span>
                <span className="hidden sm:block text-left leading-tight">
                  <span className="block text-sm text-white font-medium max-w-[140px] truncate">{user?.full_name || user?.username}</span>
                  <span className="block text-[11px] text-[#C6A15B]">{ROLE_LABELS[role] || role}</span>
                </span>
                <ChevronDown className="w-4 h-4 text-slate-500 hidden sm:block" />
              </button>

              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setUserMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 w-56 bg-[#14171B] border border-white/[0.08] rounded-xl shadow-2xl z-40 overflow-hidden" data-testid="user-menu">
                    <div className="px-4 py-3 border-b border-white/[0.06]">
                      <p className="text-sm text-white font-medium truncate">{user?.full_name || user?.username}</p>
                      <p className="text-xs text-slate-400 truncate">{user?.company_name || user?.email}</p>
                    </div>
                    <button
                      onClick={() => { setUserMenuOpen(false); setProfileOpen(true); }}
                      className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-slate-300 hover:bg-white/[0.04]"
                      data-testid="profile-btn"
                    >
                      <UserCircle className="w-4 h-4" /> Profile
                    </button>
                    <button
                      onClick={() => { setUserMenuOpen(false); onLogout(); }}
                      className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 border-t border-white/[0.06]"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4" /> {t('logout')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="px-4 sm:px-6 lg:px-8 py-6">
          {children}
        </main>
      </div>

      {/* Read-only account/profile view (reuses existing authenticated user data) */}
      {profileOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setProfileOpen(false)}>
          <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="profile-dialog">
            <div className="flex items-start justify-between p-5 border-b border-white/[0.06]">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-full bg-gradient-to-br from-[#C6A15B] to-[#8a6f3a] text-black text-sm font-bold flex items-center justify-center">{initials}</span>
                <div>
                  <h3 className="text-base font-semibold text-white">{user?.full_name || user?.username}</h3>
                  <p className="text-xs text-[#C6A15B]">{ROLE_LABELS[role] || role}</p>
                </div>
              </div>
              <button onClick={() => setProfileOpen(false)} className="text-slate-500 hover:text-slate-200" data-testid="profile-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              {[
                ['Username', user?.username],
                ['Email', user?.email],
                ['Company', user?.company_name],
                ['Phone', user?.phone],
                ['Address', user?.address],
                ['Account Type', ROLE_LABELS[role] || role],
              ].filter(([, v]) => v).map(([label, val]) => (
                <div key={label} className="flex items-start justify-between gap-4">
                  <span className="text-xs text-slate-500 pt-0.5">{label}</span>
                  <span className="text-sm text-slate-200 text-right break-words max-w-[65%]">{val}</span>
                </div>
              ))}
              <p className="text-xs text-slate-500 pt-3 border-t border-white/[0.06]">To update your account details, please contact your TC administrator.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppShell;
